// @ts-nocheck
/**
 * LoginForm Component
 * CRITICAL: Core authentication component for Sigma08 platform
 * Features: MFA support, Remember Me, Password visibility toggle, WCAG 2.1 AA compliance
 * Architecture: Following authentication-identity-management epic specifications
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Form,
  FormGroup,
  TextInput,
  PasswordInput,
  Button,
  Checkbox,
  InlineNotification,
  Stack,
  Loading,
  ProgressIndicator,
  ProgressStep,
  Link,
} from '@carbon/react';
import {
  User,
  Password,
  Security,
  Email,
  WarningAltFilled,
  View,
  ViewOff,
  Login,
  Information,
  Help,
} from '@carbon/icons-react';
import { message } from '../../utils/notificationHelpers';
import type {
  ILoginFormValues,
  ILoginFormProps,
  IAuthenticationRequest,
  IAuthenticationResponse,
  IMFAChallenge,
  IAuthError,
} from '../../../../shared/interfaces/auth';
import { authService } from '../../services/authService';
import { useAuthStore } from '../../store/authStore';
import styles from './LoginForm.module.scss';

/**
 * LoginForm Component
 * Provides secure authentication with MFA support and WCAG 2.1 AA compliance
 */
const LoginForm: React.FC<ILoginFormProps> = ({
  onSuccess,
  onError,
  redirectUrl = '/dashboard',
  showRegistrationLink = true,
  showForgotPassword = true,
}) => {
  const navigate = useNavigate();
  const { setUser } = useAuthStore();
  const [formValues, setFormValues] = useState<ILoginFormValues>({
    email: '',
    password: '',
    remember_me: false,
    mfa_code: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState<IMFAChallenge | null>(null);
  const [tempToken, setTempToken] = useState<string>('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutTime, setLockoutTime] = useState(0);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Maximum login attempts before temporary lockout
  const MAX_ATTEMPTS = 5;
  const LOCKOUT_DURATION = 300000; // 5 minutes in milliseconds

  // Check for lockout status on component mount
  useEffect(() => {
    const storedLockout = localStorage.getItem('loginLockout');
    if (storedLockout) {
      const lockoutData = JSON.parse(storedLockout);
      const timePassed = Date.now() - lockoutData.timestamp;
      if (timePassed < LOCKOUT_DURATION) {
        setIsLocked(true);
        setLockoutTime(LOCKOUT_DURATION - timePassed);
        startLockoutTimer(LOCKOUT_DURATION - timePassed);
      } else {
        localStorage.removeItem('loginLockout');
      }
    }

    // Load saved email if Remember Me was checked
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setFormValues(prev => ({ ...prev, email: savedEmail, remember_me: true }));
    }
  }, []);

  // Start lockout countdown timer
  const startLockoutTimer = (duration: number) => {
    const interval = setInterval(() => {
      setLockoutTime(prev => {
        if (prev <= 1000) {
          clearInterval(interval);
          setIsLocked(false);
          localStorage.removeItem('loginLockout');
          return 0;
        }
        return prev - 1000;
      });
    });
  };

  // Handle caps lock detection
  const handleKeyEvent = (e: React.KeyboardEvent) => {
    if (e.getModifierState) {
      setCapsLockOn(e.getModifierState('CapsLock'));
    }
  };

  // Calculate password strength for visual feedback
  const calculatePasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 25;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
    if (/\d/.test(password) && /[^a-zA-Z\d]/.test(password)) strength += 25;
    setPasswordStrength(strength);
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formValues.email) {
      errors.email = 'Please enter your email address';
    } else if (!/\S+@\S+\.\S+/.test(formValues.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formValues.password) {
      errors.password = 'Please enter your password';
    } else if (formValues.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    if (mfaRequired && !formValues.mfa_code) {
      errors.mfa_code = 'Please enter your authentication code';
    } else if (mfaRequired && formValues.mfa_code && !/^\d{6}$/.test(formValues.mfa_code)) {
      errors.mfa_code = 'Code must be 6 digits';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isLocked) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrorMessage(''); // Clear any previous error messages

    try {
      // Save email if Remember Me is checked
      if (formValues.remember_me) {
        localStorage.setItem('rememberedEmail', formValues.email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }

      // If MFA is required and we have the MFA code, verify MFA
      if (mfaRequired && tempToken) {
        // Build MFA verification request
        const mfaRequest: any = {
          temp_token: tempToken,
        };

        // Add either TOTP code or backup code
        if (useBackupCode) {
          if (!formValues.mfa_code || formValues.mfa_code.length !== 8) {
            throw new Error('Backup code must be 8 characters');
          }
          mfaRequest.backup_code = formValues.mfa_code;
        } else {
          if (!formValues.mfa_code || !/^\d{6}$/.test(formValues.mfa_code)) {
            throw new Error('Authentication code must be 6 digits');
          }
          mfaRequest.code = formValues.mfa_code;
        }

        // Call MFA verification endpoint
        // EPIC17 BUGFIX: Removed /v1 suffix - backend endpoints are at /api not /api/v1
        const mfaResponse = await fetch(`${process.env.VITE_API_BASE_URL || ''}/api/auth/mfa/verify-login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(mfaRequest),
        });

        if (!mfaResponse.ok) {
          const errorData = await mfaResponse.json();
          throw new Error(errorData.message || 'MFA verification failed');
        }

        const mfaData = await mfaResponse.json();

        // Store tokens
        if (mfaData.accessToken || mfaData.tokens?.access_token) {
          const accessToken = mfaData.accessToken || mfaData.tokens.access_token;
          const refreshToken = mfaData.refreshToken || mfaData.tokens.refresh_token;

          localStorage.setItem('access_token', accessToken);
          localStorage.setItem('auth_token', accessToken);
          localStorage.setItem('sigma08_jwt_token', accessToken);
          if (refreshToken) {
            localStorage.setItem('refresh_token', refreshToken);
          }
        }

        // Update the auth store with the logged in user
        setUser(mfaData.user || mfaData.data);

        // Create response in expected format
        const response: IAuthenticationResponse = {
          access_token: mfaData.accessToken || mfaData.tokens?.access_token || '',
          refresh_token: mfaData.refreshToken || mfaData.tokens?.refresh_token || '',
          expires_in: mfaData.tokens?.expires_in || 3600,
          user: mfaData.user || mfaData.data,
          requires_mfa: false,
        };

        // Reset login attempts on success
        setLoginAttempts(0);
        localStorage.removeItem('loginAttempts');

        // Wait for token storage to complete
        await new Promise(resolve => setTimeout(resolve, 500));

        // Call success callback or navigate
        if (onSuccess) {
          onSuccess(response);
        } else {
          const user = mfaData.user || mfaData.data;
          let roleBasedRedirect = redirectUrl;
          if (user.role === 'teacher') {
            roleBasedRedirect = '/teacher';
          } else if (user.role === 'platform_admin') {
            roleBasedRedirect = '/admin';
          } else if (user.role === 'principal') {
            roleBasedRedirect = '/dashboard';
          } else if (user.role === 'district_admin') {
            roleBasedRedirect = '/admin';
          } else if (user.role === 'parent') {
            roleBasedRedirect = '/parent';
          } else if (user.role === 'student') {
            roleBasedRedirect = '/student';
          } else if (user.role === 'individual') {
            roleBasedRedirect = '/dashboard';
          }
          navigate(roleBasedRedirect);
        }
        return;
      }

      // Initial login request (username + password)
      const email = formValues.email.toLowerCase();
      let detectedRole: 'admin' | 'teacher' | 'parent' | 'student' = 'student';

      // Detect role based on email patterns
      if (email.includes('admin') || email === 'admin@sigma08.com') {
        detectedRole = 'admin';
      } else if (email.includes('teacher') || email === 'teacher@sigma08.com') {
        detectedRole = 'teacher';
      } else if (email.includes('parent') || email === 'parent@sigma08.com') {
        detectedRole = 'parent';
      } else if (email.includes('student') || email === 'student@sigma08.com') {
        detectedRole = 'student';
      }

      const loginRequest = {
        email: formValues.email,
        password: formValues.password,
        role: detectedRole,
      };

      // Make API call using authService
      const loginResponse = await authService.login(loginRequest);

      // Check if MFA is required
      if ((loginResponse as any).requires_mfa) {
        setMfaRequired(true);
        setTempToken((loginResponse as any).temp_token);
        setErrorMessage('');
        setFormValues(prev => ({ ...prev, mfa_code: '' }));
        setLoading(false);
        return;
      }

      // Normal login flow (no MFA required)
      const user = loginResponse;

      // Reset login attempts on successful authentication
      setLoginAttempts(0);
      localStorage.removeItem('loginAttempts');

      // Update the auth store with the logged in user
      setUser(user);

      // CRITICAL FIX: Wait for token storage to complete FIRST before retrieving tokens
      // authService.login() calls tokenManager.setTokens() which is async and can take up to 500ms
      // We must wait for completion before trying to read the tokens
      await new Promise(resolve => setTimeout(resolve, 600));

      // VERIFICATION: Poll storage with timeout to ensure tokens are actually stored
      const verifyTokenStorage = async (maxAttempts = 20, delayMs = 50): Promise<string | null> => {
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          const storedToken =
            localStorage.getItem('access_token') ||
            sessionStorage.getItem('access_token') ||
            localStorage.getItem('auth_token') ||
            sessionStorage.getItem('auth_token') ||
            localStorage.getItem('sigma08_jwt_token') ||
            sessionStorage.getItem('sigma08_jwt_token');

          if (storedToken) {
            return storedToken;
          }

          // Wait before next attempt
          if (attempt < maxAttempts - 1) {
            await new Promise(resolve => setTimeout(resolve, delayMs));
          }
        }
        return null;
      };

      // Get tokens from storage with verification
      let access_token = await verifyTokenStorage();
      const refresh_token = localStorage.getItem('refresh_token') || sessionStorage.getItem('refresh_token') || '';

      // If token verification failed, throw error
      if (!access_token) {
        throw new Error('Login succeeded but token storage failed. Please try again.');
      }

      // Create response in expected format for callbacks
      const response: IAuthenticationResponse = {
        access_token,
        refresh_token,
        expires_in: 3600,
        user: {
          user_id: user.user_id,
          username: user.username || user.email.split('@')[0],
          email: user.email || '',
          role: user.role,
          family_id: user.family_id || '',
          permissions: user.permissions,
          age_verified: true,
          mfa_enabled: false,
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          avatar_url: user.avatar_url || '',
        },
        requires_mfa: false,
      };

      // CRITICAL FIX: Always call onSuccess callback if provided, but DON'T let it block navigation
      // The parent component's onSuccess should handle navigation
      if (onSuccess) {
        // Parent component will handle navigation through the callback
        onSuccess(response);
      } else {
        // Fallback: if no callback provided, navigate directly
        let roleBasedRedirect = redirectUrl;
        if (user.role === 'teacher') {
          roleBasedRedirect = '/teacher';
        } else if (user.role === 'platform_admin') {
          roleBasedRedirect = '/admin';
        } else if (user.role === 'principal') {
          roleBasedRedirect = '/dashboard';
        } else if (user.role === 'district_admin') {
          roleBasedRedirect = '/admin';
        } else if (user.role === 'parent') {
          roleBasedRedirect = '/parent';
        } else if (user.role === 'student') {
          roleBasedRedirect = '/student';
        } else if (user.role === 'individual') {
          roleBasedRedirect = '/dashboard';
        }

        navigate(roleBasedRedirect);
      }
    } catch (error: any) {
      // Handle authentication errors
      const authError: IAuthError = {
        error: 'authentication_failed',
        error_description: error.message || 'Invalid email or password',
      };

      // Increment login attempts
      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);
      localStorage.setItem('loginAttempts', newAttempts.toString());

      // Check if account should be locked
      if (newAttempts >= MAX_ATTEMPTS) {
        setIsLocked(true);
        setLockoutTime(LOCKOUT_DURATION);
        localStorage.setItem('loginLockout', JSON.stringify({ timestamp: Date.now(), attempts: newAttempts }));
        startLockoutTimer(LOCKOUT_DURATION);
        setErrorMessage(
          `Too many failed attempts. Account locked for ${Math.ceil(LOCKOUT_DURATION / 1000 / 60)} minutes.`
        );
      } else {
        // Show error message with remaining attempts
        setErrorMessage(`${authError.error_description} (${MAX_ATTEMPTS - newAttempts} attempts remaining)`);
      }

      // Call error callback if provided
      if (onError) {
        onError(authError);
      }
    } finally {
      setLoading(false);
    }
  };

  // Get password strength color
  const getPasswordStrengthColor = () => {
    if (passwordStrength < 50) return 'var(--cds-support-error)';
    if (passwordStrength < 75) return 'var(--cds-support-warning)';
    return 'var(--cds-support-success)';
  };

  // Handle input changes
  const handleInputChange = (field: keyof ILoginFormValues, value: string | boolean) => {
    setFormValues(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    // Calculate password strength if password field
    if (field === 'password' && typeof value === 'string') {
      calculatePasswordStrength(value);
    }
  };

  // Debug: Log when LoginForm component renders

  return (
    <div className={styles['login-form-container']}>
      {/* Form Header */}
      <Stack gap={4} className={styles['form-header']}>
        <h2 className={`cds--productive-heading-04 ${styles['form-title']}`}>
          <Login size={24} className={styles['title-icon']} />
          Sign In
        </h2>
        <p className={styles['form-subtitle']}>Welcome back to Sigma08 Learning Platform</p>
      </Stack>

      {/* Lockout Warning */}
      {isLocked && (
        <InlineNotification
          kind="error"
          title="Account Temporarily Locked"
          subtitle={`Too many failed login attempts. Please wait ${Math.ceil(
            lockoutTime / 1000
          )} seconds before trying again.`}
          lowContrast
          className={styles['notification']}
        />
      )}

      {/* Error Notification */}
      {errorMessage && !isLocked && (
        <InlineNotification
          kind="error"
          title="Login Failed"
          subtitle={errorMessage}
          lowContrast
          onClose={() => setErrorMessage('')}
          className={styles['notification']}
        />
      )}

      {/* Login Form */}
      <Form onSubmit={handleSubmit}>
        <Stack gap={5}>
          {/* Email Field */}
          <TextInput
            id="email"
            labelText="Email Address"
            placeholder="Enter your email address"
            value={formValues.email}
            onChange={e => handleInputChange('email', e.target.value)}
            invalid={!!formErrors.email}
            invalidText={formErrors.email}
            disabled={loading || isLocked}
            autoComplete="email"
            aria-label="Email Address"
            aria-required="true"
          />

          {/* Password Field */}
          <div className={styles['password-field-container']}>
            <PasswordInput
              id="password"
              labelText={
                <span>
                  Password
                  {capsLockOn && <span className={styles['caps-lock-warning']}>Caps Lock is ON</span>}
                </span>
              }
              placeholder="Enter password"
              value={formValues.password}
              onChange={e => handleInputChange('password', e.target.value)}
              onKeyDown={handleKeyEvent}
              onKeyUp={handleKeyEvent}
              invalid={!!formErrors.password}
              invalidText={formErrors.password}
              disabled={loading || isLocked}
              autoComplete="current-password"
              aria-label="Password"
              aria-required="true"
              showPasswordLabel="Show password"
              hidePasswordLabel="Hide password"
            />

            {/* Password Strength Indicator */}
            {formValues.password && (
              <div className={styles['password-strength-wrapper']}>
                <span className={`cds--caption-01 ${styles['password-strength-label']}`}>Password Strength:</span>
                <ProgressIndicator
                  currentIndex={Math.floor(passwordStrength / 25)}
                  className={styles['progress-indicator-wrapper']}
                >
                  <ProgressStep label="" secondaryLabel="" complete={passwordStrength >= 25} />
                  <ProgressStep label="" secondaryLabel="" complete={passwordStrength >= 50} />
                  <ProgressStep label="" secondaryLabel="" complete={passwordStrength >= 75} />
                  <ProgressStep label="" secondaryLabel="" complete={passwordStrength >= 100} />
                </ProgressIndicator>
              </div>
            )}
          </div>

          {/* MFA Code Field (shown when required) */}
          {mfaRequired && (
            <>
              <TextInput
                id="mfa_code"
                labelText={
                  <span>
                    <Security size={16} className={styles['title-icon']} />
                    {useBackupCode ? 'Backup Code' : 'Authentication Code'}
                  </span>
                }
                placeholder={useBackupCode ? 'Enter 8-character backup code' : 'Enter 6-digit code'}
                value={formValues.mfa_code || ''}
                onChange={e => handleInputChange('mfa_code', e.target.value)}
                invalid={!!formErrors.mfa_code}
                invalidText={formErrors.mfa_code}
                maxLength={useBackupCode ? 8 : 6}
                disabled={loading || isLocked}
                autoComplete="one-time-code"
                aria-label={useBackupCode ? 'MFA backup code' : 'Multi-factor authentication code'}
                aria-required="true"
                helperText={
                  useBackupCode
                    ? 'Enter one of your saved backup codes'
                    : 'Enter the 6-digit code from your authenticator app'
                }
              />
              <div className={styles['mfa-toggle-link']}>
                <Link
                  href="#"
                  size="sm"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    setUseBackupCode(!useBackupCode);
                    setFormValues(prev => ({ ...prev, mfa_code: '' }));
                    setFormErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.mfa_code;
                      return newErrors;
                    });
                  }}
                >
                  {useBackupCode ? 'Use authenticator code instead' : 'Use backup code instead'}
                </Link>
              </div>
              <InlineNotification
                kind="info"
                title="MFA Verification Required"
                subtitle={
                  useBackupCode
                    ? 'Use one of your backup codes if you lost access to your authenticator app'
                    : 'Two-factor authentication is enabled for your account'
                }
                lowContrast
                hideCloseButton
                className={styles['mfa-info-notification']}
              />
            </>
          )}

          {/* Remember Me and Forgot Password */}
          <div className={styles['form-actions-row']}>
            <Checkbox
              id="remember_me"
              labelText="Remember me"
              checked={formValues.remember_me}
              onChange={(_, { checked }) => handleInputChange('remember_me', checked)}
              aria-label="Remember me on this device"
            />
            {showForgotPassword && (
              <Link
                href="/auth/forgot-password"
                size="sm"
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  window.location.href = '/auth/forgot-password';
                }}
              >
                Forgot password?
              </Link>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            kind="primary"
            size="lg"
            disabled={loading || isLocked}
            renderIcon={Login}
            className={styles['submit-button']}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>

          {/* OAuth Divider */}
          <div className={styles['oauth-divider']}>
            <div className={styles['divider-line']} />
            <span className={styles['divider-text']}>Or continue with</span>
          </div>

          {/* Google OAuth Button */}
          <Button
            kind="tertiary"
            size="lg"
            disabled={loading || isLocked}
            className={styles['oauth-button']}
            onClick={() => {
              const apiUrl = process.env.VITE_API_BASE_URL || '';
              // EPIC17 BUGFIX: Removed /v1 suffix - backend endpoints are at /api not /api/v1
              window.location.href = `${apiUrl}/api/auth/oauth/google`;
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={styles['oauth-button-icon']}
            >
              <path
                d="M19.6 10.227c0-.709-.064-1.39-.182-2.045H10v3.868h5.382a4.6 4.6 0 01-1.996 3.018v2.51h3.232c1.891-1.742 2.982-4.305 2.982-7.35z"
                fill="#4285F4"
              />
              <path
                d="M10 20c2.7 0 4.964-.895 6.618-2.423l-3.232-2.509c-.895.6-2.04.955-3.386.955-2.605 0-4.81-1.76-5.595-4.123H1.064v2.59A9.996 9.996 0 0010 20z"
                fill="#34A853"
              />
              <path
                d="M4.405 11.9c-.2-.6-.314-1.24-.314-1.9 0-.66.114-1.3.314-1.9V5.51H1.064A9.996 9.996 0 000 10c0 1.614.386 3.14 1.064 4.49l3.34-2.59z"
                fill="#FBBC04"
              />
              <path
                d="M10 3.977c1.468 0 2.786.505 3.823 1.496l2.868-2.868C14.959.99 12.695 0 10 0 6.09 0 2.71 2.24 1.064 5.51l3.34 2.59C5.19 5.736 7.395 3.977 10 3.977z"
                fill="#EA4335"
              />
            </svg>
            Continue with Google
          </Button>

          {/* Microsoft OAuth Button */}
          <Button
            kind="tertiary"
            size="lg"
            disabled={loading || isLocked}
            className={styles['oauth-button']}
            onClick={() => {
              const apiUrl = process.env.VITE_API_BASE_URL || '';
              // EPIC17 BUGFIX: Removed /v1 suffix - backend endpoints are at /api not /api/v1
              window.location.href = `${apiUrl}/api/auth/oauth/microsoft`;
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={styles['oauth-button-icon']}
            >
              <rect width="9" height="9" fill="#F25022" />
              <rect x="11" width="9" height="9" fill="#7FBA00" />
              <rect y="11" width="9" height="9" fill="#00A4EF" />
              <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
            </svg>
            Continue with Microsoft
          </Button>

          {/* LinkedIn OAuth Button */}
          <Button
            kind="tertiary"
            size="lg"
            disabled={loading || isLocked}
            className={styles['oauth-button']}
            onClick={() => {
              const apiUrl = process.env.VITE_API_BASE_URL || '';
              // EPIC17 BUGFIX: Removed /v1 suffix - backend endpoints are at /api not /api/v1
              window.location.href = `${apiUrl}/api/auth/oauth/linkedin`;
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={styles['oauth-button-icon']}
            >
              <rect width="20" height="20" rx="2" fill="#0A66C2" />
              <path d="M6 8.5H3.5V16H6V8.5Z" fill="white" />
              <path
                d="M4.75 3C3.784 3 3 3.784 3 4.75C3 5.716 3.784 6.5 4.75 6.5C5.716 6.5 6.5 5.716 6.5 4.75C6.5 3.784 5.716 3 4.75 3Z"
                fill="white"
              />
              <path
                d="M17 16H14.5V12C14.5 10.619 13.381 9.5 12 9.5C10.619 9.5 9.5 10.619 9.5 12V16H7V8.5H9.5V9.75C10.207 9.012 11.164 8.5 12.25 8.5C14.321 8.5 16 10.179 16 12.25V16H17Z"
                fill="white"
              />
            </svg>
            Continue with LinkedIn
          </Button>

          {/* Registration Link */}
          {showRegistrationLink && (
            <>
              <div className={styles['registration-divider']}>
                <div className={styles['divider-line']} />
                <span className={styles['registration-divider-text']}>New to Sigma08?</span>
              </div>
              <div className={styles['registration-link-wrapper']}>
                <Link
                  href="/auth/register"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    window.location.href = '/auth/register';
                  }}
                >
                  Create an account
                </Link>
              </div>
            </>
          )}

          {/* Security Notice */}
          <div className={styles['security-notice']}>
            <p className={`cds--caption-01 ${styles['security-notice-text']}`}>
              <Security size={16} className={styles['security-icon']} />
              Your login is protected by industry-standard encryption. We never store your password in plain text.
            </p>
          </div>

          {/* WCAG 2.1 AA Compliance Notice */}
          <div className={styles['wcag-notice']}>
            <span className={`cds--caption-01 ${styles['wcag-notice-text']}`}>
              This form is WCAG 2.1 AA compliant for accessibility
            </span>
          </div>
        </Stack>
      </Form>
    </div>
  );
};

export default LoginForm;
